/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { lazy, Suspense } from "react";
import {
  Route,
  Switch,
  Redirect,
  BrowserRouter,
  HashRouter,
  useHistory,
} from "react-router-dom";

import ScrollToTop from "./ScrollToTop";

import { lazyWithRetry } from "./ComponentLoader";
import { RouteWithSidebar } from "@vlinder-web/route-with-sidebar-widget-react";
import { PrivateRoute } from "@vlinder-web/private-route-widget-react";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { StateOSD } from "@vlinder-web/state-osd-v2-widget-react";
import { VApplication } from "../application";
import { AppNavigatorService } from "../services";
import { useSMAppNavigator } from "./machines/pageContent";

//NOTE: Don't do lazy binding for landing page from v2 widget
import LandingPage from "../pages/Landing";
import toast from "react-hot-toast";
import { DEFAULT_APP_CONFIG } from "../config/default.config";

//Pages
const SignInPage = lazyWithRetry(() => import("../pages/SignIn"));
const SignUpPage = lazyWithRetry(() => import("../pages/SignUp"));
const ForgotPasswordPage = lazyWithRetry(
  () => import("../pages/ForgotPassword")
);
const ResetPasswordPage = lazyWithRetry(() => import("../pages/ResetPassword"));
const LockAccountPage = lazyWithRetry(() => import("../pages/Lock"));
const HoldersBatchImportPage = lazyWithRetry(
  () => import("../pages/HoldersBatchImport")
);
const UsersBatchImportPage = lazyWithRetry(
  () => import("../pages/UsersBatchImport")
);

//Sidebar Pages
const ProfilePage = lazyWithRetry(() => import("../pages/Profile"));
const UsersListPage = lazyWithRetry(() => import("../pages/UsersList"));
const HolderGroupsPage = lazyWithRetry(() => import("../pages/HolderGroups"));
const DashboardOverviewPage = lazyWithRetry(
  () => import("../pages/DashboardOverview")
);
const DashboardIssuerAnalysisPage = lazyWithRetry(
  () => import("../pages/DashboardIssuerAnalysis")
);
const HoldersListPage = lazyWithRetry(() => import("../pages/HoldersList"));
const CredentialPage = lazyWithRetry(() => import("../pages/Credential"));
const IssuedCredentialPage = lazyWithRetry(
  () => import("../pages/IssuedCredentials")
);
const DownloadCertificatesPage = lazyWithRetry(
  () => import("../pages/DownloadCertificates")
);
const CredentialListPage = lazyWithRetry(
  () => import("../pages/CredentialList")
);
const IssueNewCredentialPage = lazyWithRetry(
  () => import("../pages/IssueNewCredential")
);
const IssueBulkCredentialPage = lazyWithRetry(
  () => import("../pages/IssueBulkCredential")
);
const IssueBulkCredentialSheetPage = lazyWithRetry(
  () => import("../pages/IssueBulkCredentialSheet")
);
const IssuedNetworkPage = lazyWithRetry(
  () => import("../pages/IssuedNetworkGraph")
);
const ImportStudentsLogsPage = lazyWithRetry(
  () => import("../pages/ImportStudentsLogs")
);

const CreateCredentialTemplatePage = lazyWithRetry(
  () => import("../pages/CreateCredentialTemplate")
);

const PlaceholderPage = lazyWithRetry(() => import("../pages/Placeholder"));

//Privacy and Terms Pages

const TermsPage = lazyWithRetry(() => import("../pages/Terms"));
const PrivacyPage = lazyWithRetry(() => import("../pages/Privacy"));

//ErrorPages
const NotFoundPage = lazyWithRetry(() => import("../pages/Notfound"));
const ServerErrorPage = lazyWithRetry(() => import("../pages/ServerError"));

const MyCredentialPage = lazyWithRetry(() => import("../pages/MyCredentials"));

const CreatePortfolioPage = lazyWithRetry(
  () => import("../pages/CreatePortfolio")
);

const TemplatesPage = lazyWithRetry(() => import("../pages/Templates"));

const HolderServicePage = lazyWithRetry(
  () => import("../pages/HolderServices")
);

const PortfolioListPage = lazyWithRetry(() =>import("../pages/PortfolioTemplateList"));
const CreatePortfolioTemplatePage = lazyWithRetry(() => import("../pages/CreatePortfolioTemplate"));
const CreatePortfolio = lazyWithRetry(()=>import("../pages/CreatePortfolio"));
const CreatedPortfolioPage = lazyWithRetry(()=>import("../pages/CreatedPortfolio"));
const DraftPortfolioPage = lazyWithRetry(()=>import("../pages/DraftPortfolio"));
const ReviewPortfolioHolderPage = lazyWithRetry(()=>import("../pages/ReviewPortfolioHolder"));
const ListedPortfolioPage = lazyWithRetry(()=>import("../pages/ListedPortfolio"));
const PortfolioUnderReviewPage = lazyWithRetry(()=>import("../pages/PortfolioUnderReview"));
const ReviewPortfolioPage = lazyWithRetry(()=>import("../pages/ReviewPortfolio"));
const PortfolioForReviewPage = lazyWithRetry(()=>import("../pages/PortfolioForReview"));
const UserProfilePage = lazyWithRetry(()=>import("../pages/UserProfile"));
const getAppNavService = () => {
  const app = VApplication.getInstance();
  return app?.getSync("services.AppNavigatorService") as AppNavigatorService;
};

const RouterConfig = () => {
  const { appNavigatorSend, appNavigatorState, appNavigatorMachine } =
    useSMAppNavigator({});
  const appNavSrv = getAppNavService();
  const history = useHistory();

  const _handleDropDownItemClick = (item: any) => {
    const storageSrv = StorageModule?.getInstance();

    console.log("on DD Item click =>", item);
    if (item.id === "logout") {
      storageSrv.reset("session");
      storageSrv.reset("local");
      storageSrv.reset("cookie");
      storageSrv.reset("memory");
      window.open("/", "_self");
    } else {
      toast.success("Coming soon!");
    }
  };

  if (appNavigatorState?.matches("RENDER_PAGE")) {
    const {
      brandLogo,
      brandLogoLink,
      brandTitle,
      sidebarItems,
      routes,
      orgName,
      orgUrl,
      copyrightStartYear,
      whoami,
      userName,
      userImage,
      dropdownItems,
      footerSection,
    } = { ...(appNavigatorState?.event?.data?.value || {}) };
    const canAccessHoldersBatchImport = () => {
      return (
        appNavSrv?.abilityCheck("HoldersBatchImport", whoami, "read") ||
        appNavSrv?.abilityCheck("Holders", whoami, "read")
      );
    };
    return (
      <Switch>
        <Route exact path="/" component={LandingPage} />
           <Route exact path={routes?.SignIn?.path} component={SignInPage} />
        <Route exact path={routes?.SignUp?.path} component={SignUpPage} />
        <Route exact path={routes?.Privacy?.path} component={PrivacyPage} />
        <Route exact path={routes?.Terms?.path} component={TermsPage} />

        <Route
          exact
          path={routes?.ResetPassword?.path}
          component={ResetPasswordPage}
        />
        <Route
          exact
          path={routes?.ForgotPassword?.path}
          component={ForgotPasswordPage}
        />
        <Route exact path={routes?.Lock?.path} component={LockAccountPage} />
        <Route
          exact
          path={routes?.ServerError?.path}
          component={ServerErrorPage}
        />
        <Route exact path={routes?.NotFound?.path} component={NotFoundPage} />

        <RouteWithSidebar
          exact
          path={routes?.OrgProfile?.path}
          component={ProfilePage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.OrgProfile?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.MyProfile?.path}
          component={UserProfilePage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MyProfile?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.DashboardOverview?.path}
          component={DashboardOverviewPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DashboardOverview?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.DashboardIssuerAnalysis?.path}
          component={DashboardIssuerAnalysisPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DashboardIssuerAnalysis?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.DashboardVerifierAnalysis?.path}
          component={DashboardIssuerAnalysisPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DashboardVerifierAnalysis?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.UsersList?.path}
          component={UsersListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.UsersList?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.HoldersGroups?.path}
          component={HolderGroupsPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.HoldersGroups?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.["HoldersGroups.Holders"]?.path}
          component={HoldersListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.["HoldersGroups.Holders"]?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.Holders?.path}
          component={HoldersListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Holders?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path="/holders/batch-import"
          component={HoldersBatchImportPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={canAccessHoldersBatchImport}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
        />
        <RouteWithSidebar
          exact
          path="/holders/import-holders"
          component={HoldersBatchImportPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={canAccessHoldersBatchImport}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
        />
        <RouteWithSidebar
          exact
          path={routes?.IssuedCredentialList?.path}
          component={IssuedCredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.IssuedCredentialList?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.DownloadCertificates?.path}
          component={DownloadCertificatesPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DownloadCertificates?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.Templates?.path}
          component={CredentialListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Templates?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.CredentialList?.path}
          component={CredentialListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CredentialList?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.Services?.path}
          component={HolderServicePage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Services?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
        />
        <RouteWithSidebar
          exact
          path={routes?.MyCredentials?.path}
          component={MyCredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.MyCredentials?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.IssueNewCredential?.path}
          component={IssueNewCredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.IssueNewCredential?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.IssueBulkCredential?.path}
          component={IssueBulkCredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.IssueBulkCredential?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
        />
        <RouteWithSidebar
          exact
          path={routes?.UploadLogs?.path}
          component={ImportStudentsLogsPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.UploadLogs?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
        />
        <RouteWithSidebar
          exact
          path={routes?.IssueBulkCredentialSheet?.path}
          component={IssueBulkCredentialSheetPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.IssueBulkCredentialSheet?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
        />
        <RouteWithSidebar
          exact
          path={routes?.IssueCredential?.path}
          component={IssueNewCredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.IssueNewCredential?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.Credential?.path}
          component={CredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Credential?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.VerifyPortfolioCredential?.path}
          component={CredentialPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.VerifyPortfolioCredential?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.IssuedNetworkGraph?.path}
          component={IssuedNetworkPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.IssuedNetworkGraph?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.Placeholder?.path}
          component={PlaceholderPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.Placeholder?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.CreateCredentialTemplate?.path}
          component={CreateCredentialTemplatePage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CreateCredentialTemplate?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
         <RouteWithSidebar
          exact
          path={routes?.CreatedEPortfolio?.path}
          component={CreatedPortfolioPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CreatedEPortfolio?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        {/* <Route
          exact
          path={routes?.CreatedPortfolioPage?.path}
          component={CreatedPortfolioPage}
        /> */}
        <RouteWithSidebar
          exact
          path={routes?.ReviewedEPortfolio?.path}
          component={ReviewPortfolioHolderPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ReviewedEPortfolio?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.PortfoliosForReview?.path}
          component={PortfolioForReviewPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PortfoliosForReview?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.ListedEPortfolio?.path}
          component={ListedPortfolioPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ListedEPortfolio?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
          <RouteWithSidebar
          exact
          path={routes?.PortfolioList?.path}
          component={PortfolioListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PortfolioList?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.PortfolioTemplates?.path}
          component={PortfolioListPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PortfolioTemplates?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
          <RouteWithSidebar
          exact
          path={routes?.CreateEPortfolio?.path}
          component={CreatePortfolioPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CreateEPortfolio?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
         <RouteWithSidebar
          exact
          path={routes?.DraftEPortfolio?.path}
          component={DraftPortfolioPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.DraftEPortfolio?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.ReviewEPortfolio?.path}
          component={ReviewPortfolioPage}
          // component={PortfolioCollectionPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ReviewEPortfolio?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.ListedEPortfolioDetail?.path}
          component={ReviewPortfolioPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.ListedEPortfolioDetail?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        <RouteWithSidebar
          exact
          path={routes?.CreatePortfolioTemplate?.path}
          component={CreatePortfolioTemplatePage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.CreatePortfolioTemplate?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />
        {/* <RouteWithSidebar
          exact
          path={routes?.PortfolioUnderReview?.path}
          component={PortfolioUnderReviewPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PortfolioUnderReview?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        /> */}
        <RouteWithSidebar
          exact
          path={routes?.PortfolioUnderReview?.path}
          component={PortfolioUnderReviewPage}
          brandLogo={brandLogo}
          brandLogoLink={brandLogoLink}
          brandTitle={brandTitle}
          items={sidebarItems}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.PortfolioUnderReview?.id,
            whoami,
            "read"
          )}
          userName={userName}
          userImage={userImage}
          dropDownItems={dropdownItems}
          onDropDownItemClick={_handleDropDownItemClick}
          orgName={orgName}
          orgUrl={orgUrl}
          copyrightStartYear={copyrightStartYear}
          footerSections={footerSection}
          // sidebarStyle={{ backgroundColor: "#113DA0" }}
        />

           <PrivateRoute
          exact
          path={routes?.UsersBatch?.path}
          component={UsersBatchImportPage}
          notFoundLink={routes?.NotFound?.path}
          abilityCheck={appNavSrv?.abilityCheck.bind(
            null,
            routes?.UsersBatch?.id,
            whoami,
            "read"
          )}
        />
        <Redirect to={routes?.NotFound?.path} />
      </Switch>
    );
  }
  if (appNavigatorState?.matches("GET_PAGE_CONTENT")) {
    return <StateOSD {...(DEFAULT_APP_CONFIG?.defaultLoading || {})} />;
  }
  return null;
};

export const AppNavigator = (): JSX.Element => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Suspense
        fallback={<StateOSD {...(DEFAULT_APP_CONFIG?.defaultLoading || {})} />}
      >
        <RouterConfig />
      </Suspense>
    </BrowserRouter>
  );
};
